import { useCallback } from 'react';
import useSWR, { SWRResponse, Key, useSWRConfig } from 'swr';
import { AxiosError } from 'axios';

import {
  Parameter$getReservationFlowProduct,
  Response$getReservationFlowProduct$Status$200,
} from '../../../../openapi/generated/apiClient';
import { useClientSideApiClient } from '../../../../openapi/axiosClient';
import { captureException } from '@sentry/nextjs';

export interface UseGetReservationFlowProductParams {
  parameter?: Parameter$getReservationFlowProduct;
  isDisabled?: boolean;
}

export type UseGetReservationFlowProductValue = SWRResponse<
  Response$getReservationFlowProduct$Status$200['application/json'],
  AxiosError
>;

export const getGetReservationFlowProductKey: (params: {
  parameter: Parameter$getReservationFlowProduct;
}) => Key = ({ parameter }) => {
  return {
    path: '/api/reservation_flow/merchants/{merchant_public_id}/products/{product_public_id}',
    parameter,
  };
};

export const useGetReservationFlowProduct: (
  params: UseGetReservationFlowProductParams,
) => UseGetReservationFlowProductValue = ({ parameter, isDisabled }) => {
  const client = useClientSideApiClient();
  const key =
    !isDisabled && typeof parameter !== 'undefined'
      ? getGetReservationFlowProductKey({ parameter })
      : null;

  const fetcher: () => Promise<
    Response$getReservationFlowProduct$Status$200['application/json']
  > = async () => {
    if (isDisabled || typeof parameter === 'undefined') {
      captureException('Never');
      throw new Error('Never');
    }

    try {
      const result = await client.getReservationFlowProduct({
        parameter,
      });

      return result;
    } catch (error) {
      throw error;
    }
  };

  // 保護された場合など401エラーが発生する可能性があるため、shouldRetryOnErrorをfalseに設定する
  return useSWR(key, fetcher, { shouldRetryOnError: false });
};

export interface UseRevalidateGetReservationFlowProductParams {
  parameter: Parameter$getReservationFlowProduct;
}

export interface UseRevalidateGetReservationFlowProductValue {
  revalidateGetReservationFlowProduct: () => Promise<void>;
}

export const useRevalidateGetReservationFlowProduct: (
  params: UseRevalidateGetReservationFlowProductParams,
) => UseRevalidateGetReservationFlowProductValue = ({ parameter }) => {
  const { mutate } = useSWRConfig();

  const revalidateGetReservationFlowProduct = useCallback(async () => {
    await mutate(getGetReservationFlowProductKey({ parameter }));
  }, [parameter, mutate]);

  return { revalidateGetReservationFlowProduct };
};
